import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Tabs,
  Tab,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  ListSubheader,
  useTheme,
  Pagination,
  Tooltip,
  Menu,
  IconButton,
} from "@mui/material";
import Userdetail from "./Userdetail";
import CompantDetailes from "./CompantDetailes";
import SubscriptionDetailes from "./SubscriptionDetailes";
import axios from "axios";
import moment from "moment";
import ipconfig from "../../Core/ipconfig";
import Spinner from "../../Shared/Feature/LoadingSpinner/Spinner";
import SearchBar from "../../Shared/Widgets/SearchBar";
import { decryptData } from "../../Core/util/obfuscate";
import ErrorBoundary from "../../Shared/Widgets/ErrorBoundary";
import { formatPhoneNumber } from "../../Core/util/formatPhone";

export default function User() {
  const theme = useTheme();
  const location = useLocation();
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [tabIndex, setTabIndex] = useState(0);
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    const cached = localStorage.getItem("adminMe");
    if (cached) {
      setCurrentUser(decryptData(cached));
    }
  }, []);

  const [allUserData, setAllUserData] = useState<any[]>([]);
  const [filteredUserData, setFilteredUserData] = useState<any[]>([]);
  const [selectedUserData, setSelectedUserData] = useState<any>(null);
  
  // Filtering and Sorting States
  const [filter, setFilter] = useState("All");
  const [sortBy, setSortBy] = useState("A-Z");
  
  // Pagination States
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  const [downloadAnchorEl, setDownloadAnchorEl] = useState<null | HTMLElement>(null);
  const [exportLoading, setExportLoading] = useState(false);

  const [loading, setLoading] = useState(true);

  const handleTabChange = (event: any, newValue: any) => {
    setTabIndex(newValue);
  };

  const handleFilterChange = (event: any) => {
    setFilter(event.target.value);
  };

  const handleSortChange = (event: any) => {
    setSortBy(event.target.value);
  };

  const handleDownloadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const handleExport = async (format: "excel" | "pdf") => {
    handleDownloadClose();
    setExportLoading(true);
    try {
      const response = await axios.get(
        `${ipconfig}/getallusers?search=${encodeURIComponent(debouncedSearchValue)}&filter=${filter}&sortBy=${sortBy}&page=1&limit=999999`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const rawUsers = response?.data?.data || [];
      const usersToExport = rawUsers.map((u: any) => {
        let statusDisplay = "Not Subscribed";
        if (u.subscriptionStatus === "subscribed") {
          statusDisplay = "Subscribed";
        } else if (u.subscriptionStatus === "free-trial") {
          statusDisplay = "Free Trial";
        } else if (u.subscriptionStatus === "expired") {
          statusDisplay = "Expired";
        }
        return {
          ...u,
          statusDisplay
        };
      });

      if (format === "excel") {
        const html = `
          <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <meta charset="utf-8">
            <!--[if gte mso 9]>
            <xml>
              <x:ExcelWorkbook>
                <x:ExcelWorksheets>
                  <x:ExcelWorksheet>
                    <x:Name>Users List</x:Name>
                    <x:WorksheetOptions>
                      <x:DisplayGridlines/>
                    </x:WorksheetOptions>
                  </x:ExcelWorksheet>
                </x:ExcelWorksheets>
              </x:ExcelWorkbook>
            </xml>
            <![endif]-->
          </head>
          <body>
            <table border="1">
              <tr style="background-color: #4f46e5; color: #ffffff; font-weight: bold; height: 30px;">
                <th>Name</th>
                <th>Email</th>
                <th>Phone Number</th>
                <th>Role</th>
                <th>Subscription Status</th>
                <th>Is on Free Trial</th>
                <th>Is Free Trial Opted</th>
                <th>Subscription Start Date</th>
                <th>Subscription End Date</th>
                <th>Total Companies</th>
                <th>Joined Date</th>
              </tr>
              ${usersToExport.map((u: any) => `
                <tr style="height: 24px;">
                  <td>${u.name}</td>
                  <td>${u.email || "N/A"}</td>
                  <td>${formatPhoneNumber(u.phoneNumber, u.countryCode)}</td>
                  <td>${u.isOwner ? "Owner" : "User"}</td>
                  <td>${u.statusDisplay}</td>
                  <td>${u.isOwner ? (u.isOnFreeTrial ? "Yes" : "No") : "N/A"}</td>
                  <td>${u.isOwner ? (u.isTrialAlreadyOpted ? "Yes" : "No") : "N/A"}</td>
                  <td>${u.isOwner && u.subscriptionStartDate ? moment(u.subscriptionStartDate).format("DD-MM-YYYY") : "N/A"}</td>
                  <td>${u.isOwner && u.subscriptionEndDate ? moment(u.subscriptionEndDate).format("DD-MM-YYYY") : "N/A"}</td>
                  <td>${u.totalCompanies}</td>
                  <td>${moment(u.createdAt).format("DD-MM-YYYY")}</td>
                </tr>
              `).join("")}
            </table>
          </body>
          </html>
        `;

        const blob = new Blob([html], { type: "application/vnd.ms-excel" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Users_Report_${moment().format("YYYYMMDD_HHmmss")}.xls`;
        link.click();
        URL.revokeObjectURL(url);
      } else if (format === "pdf") {
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          const html = `
            <html>
            <head>
              <title>Users Directory Report</title>
              <style>
                body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 20px; color: #1e293b; background-color: #ffffff; }
                .header-container { border-bottom: 2px solid #e2e8f0; padding-bottom: 16px; margin-bottom: 24px; }
                h1 { margin: 0; color: #4f46e5; font-size: 24px; font-weight: 800; }
                .meta { font-size: 12px; color: #64748b; margin-top: 6px; }
                table { width: 100%; border-collapse: collapse; margin-top: 16px; }
                th, td { border: 1px solid #e2e8f0; padding: 10px 12px; text-align: left; font-size: 11px; }
                th { background-color: #f8fafc; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; }
                tr:nth-child(even) { background-color: #f8fafc50; }
                .role-badge { display: inline-block; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 700; text-transform: uppercase; }
                .role-owner { background-color: #eff6ff; color: #1d4ed8; }
                .role-user { background-color: #f0fdf4; color: #15803d; }
                @media print {
                  body { padding: 0; }
                  @page { margin: 1cm; }
                }
              </style>
            </head>
            <body>
              <div class="header-container">
                <h1>Accosync Users Directory</h1>
                <div class="meta">
                  Generated: ${moment().format("DD MMM YYYY, hh:mm A")} | Filter: <span style="text-transform: capitalize; font-weight: 600;">${filter}</span> | Sorted: <span style="text-transform: capitalize; font-weight: 600;">${sortBy}</span> | Total Records: ${usersToExport.length}
                </div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Subscription</th>
                    <th>On Trial</th>
                    <th>Trial Opted</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Companies</th>
                    <th>Joined Since</th>
                  </tr>
                </thead>
                <tbody>
                  ${usersToExport.map((u: any) => `
                    <tr>
                      <td style="font-weight: 600; color: #0f172a;">${u.name}</td>
                      <td>${u.email || "N/A"}</td>
                      <td>${formatPhoneNumber(u.phoneNumber, u.countryCode)}</td>
                      <td>
                        <span class="role-badge ${u.isOwner ? "role-owner" : "role-user"}">
                          ${u.isOwner ? "Owner" : "User"}
                        </span>
                      </td>
                      <td style="font-weight: 550;">${u.statusDisplay}</td>
                      <td>${u.isOwner ? (u.isOnFreeTrial ? "Yes" : "No") : "N/A"}</td>
                      <td>${u.isOwner ? (u.isTrialAlreadyOpted ? "Yes" : "No") : "N/A"}</td>
                      <td>${u.isOwner && u.subscriptionStartDate ? moment(u.subscriptionStartDate).format("DD MMM YYYY") : "N/A"}</td>
                      <td>${u.isOwner && u.subscriptionEndDate ? moment(u.subscriptionEndDate).format("DD MMM YYYY") : "N/A"}</td>
                      <td>${u.totalCompanies}</td>
                      <td>${moment(u.createdAt).format("DD MMM YYYY")}</td>
                    </tr>
                  `).join("")}
                </tbody>
              </table>
              <script>
                window.onload = function() {
                  window.print();
                  setTimeout(function() { window.close(); }, 500);
                };
              </script>
            </body>
            </html>
          `;
          printWindow.document.write(html);
          printWindow.document.close();
        }
      }
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExportLoading(false);
    }
  };

  const fetchUserData = async (isFirstPage = false) => {
    setLoading(true);
    try {
      const activePage = isFirstPage ? 1 : page;
      const response = await axios.get(
        `${ipconfig}/getallusers?search=${encodeURIComponent(debouncedSearchValue)}&filter=${filter}&sortBy=${sortBy}&page=${activePage}&limit=15`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const users = response?.data?.data || [];
      const pagination = response?.data?.pagination || { totalItems: users.length, totalPages: 1, currentPage: 1 };
      
      setTotalItems(pagination.totalItems);
      setTotalPages(pagination.totalPages);

      if (activePage === 1) {
        let currentUsers = users;
        let initialUser = users[0] || null;

        if (location.state && (location.state.userId || location.state.email || location.state.search)) {
          const searchEmail = (location.state.email || location.state.search || "").toLowerCase();
          let targetUser = users.find((u: any) => 
            (location.state.userId && u._id === location.state.userId) ||
            (searchEmail && u.email?.toLowerCase() === searchEmail)
          );

          if (!targetUser && location.state.userId) {
            try {
              const res = await axios.get(
                `${ipconfig}/fetchuserbyid?userId=${location.state.userId}`,
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                  },
                }
              );
              if (res.data?.data) {
                targetUser = res.data.data;
                currentUsers = [targetUser, ...users.filter((u: any) => u._id !== targetUser._id)];
              }
            } catch (err) {
              console.error("Error fetching user by id in state:", err);
            }
          }

          if (targetUser) {
            initialUser = targetUser;
            if (location.state.tab !== undefined) {
              setTabIndex(location.state.tab);
            }
          }
        }

        setAllUserData(currentUsers);
        setFilteredUserData(currentUsers);
        setSelectedUserData(initialUser);
      } else {
        setAllUserData((prev) => {
          const existingIds = new Set(prev.map((u: any) => u._id));
          const filteredNew = users.filter((u: any) => !existingIds.has(u._id));
          return [...prev, ...filteredNew];
        });
        setFilteredUserData((prev) => {
          const existingIds = new Set(prev.map((u: any) => u._id));
          const filteredNew = users.filter((u: any) => !existingIds.has(u._id));
          return [...prev, ...filteredNew];
        });
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    if (location.state && (location.state.email || location.state.search)) {
      const emailQuery = location.state.email || location.state.search;
      setSearchValue(emailQuery);
      setDebouncedSearchValue(emailQuery);
    }
  }, [location.state]);

  const prevFilter = React.useRef(filter);
  const prevSortBy = React.useRef(sortBy);
  const prevSearch = React.useRef(debouncedSearchValue);

  // Fetch user data on page, filter, sort, or search changes (ref-guarded to prevent duplicates)
  useEffect(() => {
    const filterChanged = prevFilter.current !== filter ||
                          prevSortBy.current !== sortBy ||
                          prevSearch.current !== debouncedSearchValue;

    prevFilter.current = filter;
    prevSortBy.current = sortBy;
    prevSearch.current = debouncedSearchValue;

    if (filterChanged) {
      if (page === 1) {
        fetchUserData(true);
      } else {
        setPage(1);
      }
    } else {
      fetchUserData(page === 1);
    }
  }, [page, filter, sortBy, debouncedSearchValue]);

  // Scroll to active user item
  useEffect(() => {
    if (selectedUserData && location.state?.userId === (selectedUserData as any)._id) {
       setTimeout(() => {
         const container = document.getElementById('user-list-container');
         const element = document.getElementById(`user-item-${(selectedUserData as any)._id}`);
         if (container && element) {
            const containerRect = container.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            const relativeTop = elementRect.top - containerRect.top;
            container.scrollTo({
              top: container.scrollTop + relativeTop - (container.clientHeight / 2) + (element.clientHeight / 2),
              behavior: 'smooth'
            });
          }
        }, 500);
     }
  }, [selectedUserData]);

  const renderSubscriptionStatusIcon = (status: string) => {
    switch (status) {
      case "subscribed":
        return (
          <Tooltip title="Subscribed License Owner">
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-crown">
                <path d="M2 4 5 12h14l3-8-7 4-3-6-3 6-7-4z"/>
                <path d="M3 20h18a1 1 0 0 0 1-1v-1a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1z"/>
              </svg>
            </Box>
          </Tooltip>
        );
      case "free-trial":
        return (
          <Tooltip title="Free Trial License Owner">
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-sparkles">
                <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
                <path d="m5 3 1 2.5L8.5 6 6 7 5 9.5 4 7 1.5 6 4 5.5z"/>
                <path d="m19 17 1 2.5 2.5.5-2.5 1-1 2.5-1-2.5-2.5-1 2.5-1z"/>
              </svg>
            </Box>
          </Tooltip>
        );
      case "expired":
        return (
          <Tooltip title="Expired License Owner">
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-alert-triangle">
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </Box>
          </Tooltip>
        );
      case "not-subscribed":
      default:
        return (
          <Tooltip title="Not Subscribed License Owner">
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x-circle">
                <circle cx="12" cy="12" r="10"/>
                <path d="m15 9-6 6"/>
                <path d="m9 9 6 6"/>
              </svg>
            </Box>
          </Tooltip>
        );
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (target.scrollHeight - target.scrollTop <= target.clientHeight + 30) {
      if (!loading && page < totalPages) {
        setPage((prev) => prev + 1);
      }
    }
  };

  return (
    <Box sx={{ display: "flex", width: "100%", height: "100%", gap: "10px", p: 1 }}>
      {/* Left Side: Search Bar + User List */}
      <Box sx={{ width: "320px", display: "flex", flexDirection: "column", gap: "12px", flexShrink: 0 }}>
        <Box sx={{ width: "100%" }}>
          <SearchBar onSearch={setSearchValue} searchIcon="user" />
        </Box>

        <Box
          sx={{
            backgroundColor: "background.paper",
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "12px 16px",
              borderBottom: "1px solid",
              borderColor: "divider",
              gap: "8px"
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <Typography sx={{ color: "text.primary", fontSize: "14px", fontWeight: 700 }}>
                Users ({totalItems})
              </Typography>
              <Tooltip title="Download Report">
                <IconButton
                  size="small"
                  onClick={handleDownloadClick}
                  sx={{ color: "primary.main", p: 0.5 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-download">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                </IconButton>
              </Tooltip>
            </Box>
            
            <Box sx={{ display: "flex", gap: "8px", width: "100%" }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel id="user-filter-label" sx={{ fontSize: "12.5px" }}>Filter</InputLabel>
                <Select
                  labelId="user-filter-label"
                  value={filter}
                  label="Filter"
                  onChange={handleFilterChange}
                  sx={{ fontSize: "12px" }}
                >
                  <MenuItem value="All" sx={{ fontSize: "12px" }}>All Status</MenuItem>
                  <MenuItem value="Owner" sx={{ fontSize: "12px" }}>Owners Only</MenuItem>
                  <MenuItem value="Users" sx={{ fontSize: "12px" }}>Users Only</MenuItem>
                  <MenuItem value="free-trial" sx={{ fontSize: "12px" }}>Free Trial</MenuItem>
                  <MenuItem value="subscribed" sx={{ fontSize: "12px" }}>Subscribed</MenuItem>
                  <MenuItem value="expired" sx={{ fontSize: "12px" }}>Expired</MenuItem>
                  <MenuItem value="not-subscribed" sx={{ fontSize: "12px" }}>Not Subscribed</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel id="user-sort-label" sx={{ fontSize: "12.5px" }}>Sort By</InputLabel>
                <Select
                  labelId="user-sort-label"
                  value={sortBy}
                  label="Sort By"
                  onChange={handleSortChange}
                  sx={{ fontSize: "12px" }}
                >
                  <MenuItem value="A-Z" sx={{ fontSize: "12px" }}>A-Z Name</MenuItem>
                  <MenuItem value="Z-A" sx={{ fontSize: "12px" }}>Z-A Name</MenuItem>
                  <MenuItem value="comp-desc" sx={{ fontSize: "12px" }}>High to Low Co.</MenuItem>
                  <MenuItem value="comp-asc" sx={{ fontSize: "12px" }}>Low to High Co.</MenuItem>
                  <MenuItem value="user-new-to-old" sx={{ fontSize: "12px" }}>New to Old</MenuItem>
                  <MenuItem value="user-old-to-new" sx={{ fontSize: "12px" }}>Old to New</MenuItem>
                  <MenuItem value="connector-active-latest-to-oldest" sx={{ fontSize: "12px" }}>Connector Active Latest to Oldest</MenuItem>
                  <MenuItem value="connector-active-oldest-to-latest" sx={{ fontSize: "12px" }}>Connector Active Oldest to Latest</MenuItem>
                  <MenuItem value="mobile-active-latest-to-oldest" sx={{ fontSize: "12px" }}>Mobile Active Latest to Oldest</MenuItem>
                  <MenuItem value="mobile-active-oldest-to-latest" sx={{ fontSize: "12px" }}>Mobile Active Oldest to Latest</MenuItem>
                  <MenuItem value="going-to-expire" sx={{ fontSize: "12px" }}>Expiring Soon</MenuItem>
                  <MenuItem value="subscription-latest-to-oldest" sx={{ fontSize: "12px" }}>Subscription Latest to Oldest</MenuItem>
                  <MenuItem value="subscription-oldest-to-latest" sx={{ fontSize: "12px" }}>Subscription Oldest to Latest</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {exportLoading || (loading && page === 1) ? (
            <Spinner height="100%" />
          ) : (
            <>
              <Box 
                id="user-list-container"
                onScroll={handleScroll}
                sx={{
                  flexGrow: 1,
                  overflowY: "auto",
                  padding: "8px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  "&::-webkit-scrollbar": {
                    width: "5px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "divider",
                    borderRadius: "4px",
                  },
                }}
              >
                {filteredUserData.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography sx={{ color: "text.secondary", fontSize: "13px" }}>No users found</Typography>
                  </Box>
                ) : (
                  filteredUserData.map((user: any, index: any) => {
                    const isSelected = selectedUserData && selectedUserData._id === user._id;
                    return (
                      <Box
                        key={user._id}
                        id={`user-item-${user._id}`}
                        onClick={() => setSelectedUserData(user)}
                        sx={{
                          padding: "12px",
                          display: "flex",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          backgroundColor: isSelected
                            ? theme.palette.mode === "light" ? "#f0f7ff" : "#1e3a8a33"
                            : "background.paper",
                          border: "1px solid",
                          borderColor: isSelected ? "primary.main" : "divider",
                          borderRadius: "8px",
                          alignItems: "center",
                          "&:hover": {
                            backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b80",
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        <Box sx={{ minWidth: 0, mr: 1, flexGrow: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            {user.isOwner && renderSubscriptionStatusIcon(user.subscriptionStatus)}
                            <Typography sx={{ fontWeight: 600, fontSize: "13.5px", color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {user.name} ({user.totalCompanies})
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "11px" }}>
                            {formatPhoneNumber(user.phoneNumber, user.countryCode) !== "N/A" ? formatPhoneNumber(user.phoneNumber, user.countryCode) : "No Phone Number"}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px", flexShrink: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              backgroundColor: user.isOwner ? "primary.light" : "success.light",
                              color: user.isOwner ? "primary.dark" : "success.dark",
                              px: 1.2,
                              py: 0.4,
                              borderRadius: "12px",
                            }}
                          >
                            <Typography sx={{ fontSize: "10.5px", fontWeight: 700 }}>
                              {user.isOwner ? "Owner" : "User"}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>

              {loading && page > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
                  <Spinner height="30px" />
                </Box>
              )}
            </>
          )}
        </Box>

        <Menu
          anchorEl={downloadAnchorEl}
          open={Boolean(downloadAnchorEl)}
          onClose={handleDownloadClose}
          PaperProps={{
            sx: { minWidth: "140px", borderRadius: "8px", boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)" }
          }}
        >
          <MenuItem onClick={() => handleExport("excel")} sx={{ fontSize: "13px", display: "flex", alignItems: "center", gap: "8px" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-sheet">
              <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
              <line x1="3" y1="9" x2="21" y2="9"/>
              <line x1="3" y1="15" x2="21" y2="15"/>
              <line x1="9" y1="3" x2="9" y2="21"/>
              <line x1="15" y1="3" x2="15" y2="21"/>
            </svg>
            Excel Report
          </MenuItem>
          <MenuItem onClick={() => handleExport("pdf")} sx={{ fontSize: "13px", display: "flex", alignItems: "center", gap: "8px" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-file-text">
              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/>
              <path d="M14 2v4a2 2 0 0 0 2 2h4"/>
              <path d="M10 9H8"/>
              <path d="M16 13H8"/>
              <path d="M16 17H8"/>
            </svg>
            PDF Report
          </MenuItem>
        </Menu>
      </Box>

      {/* Right Side: Tabs and Details */}
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {(() => {
          const isSuper = currentUser?.isSuperAdmin || currentUser?.superAdmin;
          const showUserDetailsTab = isSuper || currentUser?.permission?.users?.userDetails?.read;
          const showCompanyDetailsTab = isSuper || currentUser?.permission?.users?.companyDetails?.read;
          const showSubscriptionDetailsTab = isSuper || currentUser?.permission?.users?.subscriptionDetail?.read;

          const visibleTabs: { id: string; label: string }[] = [];
          if (showUserDetailsTab) {
            visibleTabs.push({ id: 'details', label: "User Details" });
          }
          if (showCompanyDetailsTab) {
            visibleTabs.push({ id: 'company', label: "Company Details" });
          }
          if (showSubscriptionDetailsTab) {
            visibleTabs.push({ id: 'subscription', label: "Subscription Details" });
          }

          const activeTabIdx = tabIndex < visibleTabs.length ? tabIndex : 0;
          const activeTabId = visibleTabs[activeTabIdx]?.id;

          return (
            <>
              {visibleTabs.length > 0 && (
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: "12px" }}>
                  <Tabs value={activeTabIdx} onChange={handleTabChange}>
                    {visibleTabs.map((t, idx) => (
                      <Tab key={t.id} label={t.label} sx={{ fontWeight: 600, textTransform: "none", fontSize: "14px" }} />
                    ))}
                  </Tabs>
                </Box>
              )}

              <Box
                sx={{
                  flexGrow: 1,
                  backgroundColor: "background.paper",
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "auto",
                  padding: "20px",
                  height: "100%",
                }}
              >
                {selectedUserData && activeTabId ? (
                  <ErrorBoundary>
                    {activeTabId === 'details' && (
                      <Userdetail
                        data={selectedUserData}
                        updatedetailes={fetchUserData}
                        readOnly={!(isSuper || currentUser?.permission?.users?.userDetails?.write)}
                      />
                    )}
                    {activeTabId === 'company' && (
                      <CompantDetailes data={selectedUserData} />
                    )}
                    {activeTabId === 'subscription' && (
                      <SubscriptionDetailes
                        data={selectedUserData}
                        readOnly={!(isSuper || currentUser?.permission?.users?.subscriptionDetail?.write)}
                      />
                    )}
                  </ErrorBoundary>
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: 500 }}>
                      {!selectedUserData ? "No user selected." : "You do not have permission to view details."}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          );
        })()}
      </Box>
    </Box>
  );
}